import "./styles.css";

import { CommodityInput } from "components/inputs/CommodityInput";
import { CoolerTypeSelect } from "components/inputs/CoolerTypeSelect";
import { CountryISOCodeInput } from "components/inputs/CountryISOCodeInput";
import { OrderReferenceInput } from "components/inputs/OrderReferenceInput";
import { PlaceSelectInput } from "components/inputs/PlaceSelectInput";
import TerminalDatesInput from "components/inputs/TerminalDatesInput";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import { ExternalTerminalConnectionStatus } from "entities/ExternalTerminalConnectionStatus";
import union from "lodash/union";
import React from "react";
import {
	ReferenceInput,
	SelectInput,
	TextInput,
	maxLength,
	useDataProvider,
	usePermissions,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { ColdStorageSection } from "resources/orders/forms/ColdStorageSection";
import { isStorageModuleEnabled } from "utils/storageModule";

import { formatPhoneNumber } from "../../../../../components/fields/PhoneField";
import {
	FormGroupGrid,
	GridForm,
	GridInputMultiChanges as GridInput,
	MultiChangesStateEnum,
	isMultiDiff,
} from "../../../../../components/GridForm";
import { DateInput, NumberInputEx, PhoneInput, TimeInput } from "../../../../../components/inputs";
import { PortInput } from "../../../../../components/inputs/PortInput";
import { SelectEnumInput } from "../../../../../components/inputs/SelectEnumInput";
import { SelectTextInput } from "../../../../../components/inputs/SelectTextInput";
import {
	SelectDisabledInput,
	SelectUppercaseInput,
} from "../../../../../components/inputs/SelectUppercaseInput";
import { ReadOnlyInput } from "../../../../../components/inputs/TextReadonlyInput";
import { YesNoInput } from "../../../../../components/inputs/YesNoInput";
import {
	LabelSource,
	OrderService,
	OrderStatus,
	OrderStatusCustomerEdit,
	OrderStatusCustomerEditOrder,
	OrderStatusEditOrderCustomerAndReference,
	OrderStatusEditOrderTerminal,
	OrderStatusTransporterEditOrder_1,
	OrderStatusTransporterEditOrder_2,
	perEligibleServices,
} from "../../../../../entities";
import HideableOrderField from "../../../../../entities/HideableOrderField";
import { useTerminalFieldVisibility } from "../../../../../hooks/useTerminalFieldVisibility";
import { isNullUndEmpty } from "../../../../../utils/general";
import { validateArea, validatePlaceLength } from "../../../../../utils/validation/order";
import { required } from "../../../../../utils/validation/required";
import { requiredIfNoFieldValue } from "../../../../../utils/validation/requiredIfNoFieldValue";
import { LoadingOrderInput } from "../../../forms/inputs/LoadingOrderInput";
import { PrintedDocsInput } from "../../../forms/inputs/PrintedDocsInput";
import { ServiceInput } from "../../../forms/inputs/ServiceInput";
import { TransporterInput } from "../../../forms/inputs/TransporterInput";
import { TripNumberInput } from "../../../forms/inputs/TripNumberInput";
import { PlumSealSection } from "../../../forms/PlumSealSection";
import {
	DangerTextInputForInternalNotes,
	ETAETDFormGroup,
	etaEtdPorts,
	useDriverChange,
	useEtaEtdSync,
	useXPartyChange,
} from "../../../forms/shared";

const rowGroupStyle = {
	borderBottom: "2px solid #bdbdbd",
	paddingBottom: 4,
	marginBottom: 4,
};

const allowFieldsEtaTrans = [
	"eta_date",
	"eta_time",
	"eta_driver",
	"eta_driver_id",
	"eta_driver_phone",
	"eta_truck",
	"eta_truck_id",
	"eta_trailer",
	"eta_trailer_id",
];
const allowFieldsEtdTrans = [
	"etd_date",
	"etd_time",
	"etd_driver",
	"etd_driver_id",
	"etd_driver_phone",
	"etd_truck",
	"etd_truck_id",
	"etd_trailer",
	"etd_trailer_id",
];

export const ETAETDBlock = ({
	isAddMode,
	prefix,
	title,
	requiredMulti,
	isFieldHidden,
	otherFieldsDisabled,
	isColdStorageService,
	isOutgoingPlukk,
	handleLabelSourceChange,
	...props
}) => {
	const { values } = useFormState();
	const byTerminalFilter = { terminal_id: [values["terminal_id"]] };
	const { data: terminals } = useTerminalsContext();
	const currentTerminal = terminals[values["terminal_id"]];
	const usesStorageModule = isStorageModuleEnabled(currentTerminal);
	const order_storage_place_field = usesStorageModule ? "storage_place_id" : "area_id";

	const externalTerminalFilter = { status: ExternalTerminalConnectionStatus.connected };
	const externalTerminalDisabled = prefix === "eta" || !currentTerminal;
	if (currentTerminal) {
		externalTerminalFilter["own_terminal_guid"] = [terminals[values["terminal_id"]].guid];
	}

	const handleDriverChange = useDriverChange(prefix);
	const [
		handle3dPartyChangeOnTruck,
		handle3dPartyChangeOnTrailer,
		handleXPartyChangeOnDriver,
		handleXPartyChangeOn4pl,
	] = useXPartyChange(prefix);
	const handleEtaEtdSync = useEtaEtdSync(prefix);

	const curr_3rd_party_ids =
		isNullUndEmpty(values[`${prefix}_3rd_party`]) &&
		props.multiData.recordData.state[`${prefix}_3rd_party`] & MultiChangesStateEnum.IS_INIT_DIFF
			? props.multiData.recordData.multiValues[`${prefix}_3rd_party`]
			: [values[`${prefix}_3rd_party`]];
	const isDisabledForEditing = curr_3rd_party_ids.some((x) => !isNullUndEmpty(x));

	const isIntoPlukkStorage = values.service === OrderService.intoPlukkStorage;
	const isCrossDock = values.service === OrderService.crossDock;

	const dateRequiredValueKeyPrefix = prefix === "eta" ? "etd" : "eta";
	const portKey = etaEtdPorts[prefix];

	const currentTransportFilter = {
		no_range: true,
		is_deleted: false,
		company_id: props.permissions.company_id || 0,
	};
	const currentTransportSort = { field: "name", order: "ASC" };

	const currentDriverFilter = {
		no_range: true,
		is_blocked: false,
		is_deleted: false,
		company_id: props.permissions.company_id || 0,
	};
	const currentDriverSort = { field: "first_name", order: "ASC" };

	const placePrefix = prefix === "eta" ? "from" : "to";
	const handle3dPartyChange = (e) => {
		handle3dPartyChangeOnTruck(e);
		handle3dPartyChangeOnTrailer(e);
		handleXPartyChangeOnDriver(e);
		handleXPartyChangeOn4pl(e);
	};

	return (
		<ETAETDFormGroup {...props} prefix={prefix}>
			{/* Row 1: Date, Time, Slot time, Port */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput
						component={DateInput}
						source={`${prefix}_date`}
						validate={[
							isIntoPlukkStorage
								? requiredIfNoFieldValue(`${dateRequiredValueKeyPrefix}_date`)
								: required(),
						]}
						onChange={handleEtaEtdSync}
					/>
				</GridInput>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput component={TimeInput} source={`${prefix}_time`} />
				</GridInput>
				<GridInput
					xs={6}
					sm={3}
					component={TimeInput}
					source={`${prefix}_slot_time`}
					disabled
					resettable
				/>
				<GridInput
					xs={6}
					sm={3}
					component={PortInput}
					source={portKey}
					hidden={isAddMode}
					filter={byTerminalFilter}
					disabled
					resettable
				/>
			</FormGroupGrid>

			{/* Row 2: Transporter, Place, Trip number, Loading order */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={TransporterInput}
					source={`${prefix}_3rd_party`}
					prefix={prefix}
					excludeIds={[props.permissions.company_id]}
					onChange={handle3dPartyChange}
					resettable
					hidden={isFieldHidden(`${prefix}_3rd_party`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					isDefaultTextMode
					component={SelectTextInput}
					sourceText={`place_${placePrefix}`}
					sourceSelect={`${prefix}_external_terminal_id`}
					reference="external-terminal-links"
					optionText="external_name"
					filter={externalTerminalFilter}
					sort={{ field: "external_name", order: "ASC" }}
					validate={[validatePlaceLength]}
					disabledSelect={externalTerminalDisabled}
					allowEmpty
					resettable
					hidden={isFieldHidden(`place_${placePrefix}`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={TextInput}
					source={`place_${placePrefix}`}
					validate={[validatePlaceLength]}
					resettable
					hidden={isFieldHidden(`place_${placePrefix}`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={CountryISOCodeInput}
					source={`place_${placePrefix}_country_code`}
					hidden={isFieldHidden(`place_${placePrefix}_country_code`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={2}
					component={YesNoInput}
					source={prefix === "eta" ? "seal" : "plumb"}
					defaultValue={false}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL : HideableOrderField.PLUMB)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={4}
					component={TextInput}
					source={prefix === "eta" ? "seal_number" : "plumb_number"}
					resettable
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL_NUMBER : HideableOrderField.PLUMB_NUMBER)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={TripNumberInput}
					source={prefix === "eta" ? "incoming_trip_number" : "outgoing_trip_number"}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.INCOMING_TRIP_NUMBER : HideableOrderField.OUTGOING_TRIP_NUMBER)}
					resettable
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={LoadingOrderInput}
					source={prefix === "eta" ? "unloading_sequence" : "loading_sequence"}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.UNLOADING_SEQUENCE : HideableOrderField.LOADING_SEQUENCE)}
					resettable
				/>
			</FormGroupGrid>

			{/* Row 3: Truck, Trailer, Driver, Phone, Show doc, NFSA doc */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={12}
					sm={6}
					md={prefix === "eta" ? 6 : 4}
					lg={prefix === "eta" ? 3 : 2}
					component={SelectTextInput}
					sourceText={`${prefix}_truck`}
					sourceSelect={`${prefix}_truck_id`}
					label={"resources.orders.fields." + prefix + "_truck"}
					reference="trucks"
					source3rdParty={`${prefix}_3rd_party`}
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					resettable
					hidden={isFieldHidden(`${prefix}_truck`)}
				/>
				<GridInput
					xs={12}
					sm={6}
					md={prefix === "eta" ? 6 : 4}
					lg={prefix === "eta" ? 3 : 2}
					component={SelectTextInput}
					sourceText={`${prefix}_trailer`}
					sourceSelect={`${prefix}_trailer_id`}
					label={"resources.orders.fields." + prefix + "_trailer"}
					reference="trailers"
					source3rdParty={`${prefix}_3rd_party`}
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					resettable
					hidden={isFieldHidden(`${prefix}_trailer`)}
				/>
				<GridInput
					xs={12}
					sm={6}
					md={4}
					lg={2}
					component={SelectTextInput}
					sourceText={`${prefix}_driver`}
					sourceSelect={`${prefix}_driver_id`}
					reference="drivers"
					optionText={(record) =>
						`${record.first_name} ${record.last_name} | ${formatPhoneNumber(record.phone)}`
					}
					filter={currentDriverFilter}
					sort={currentDriverSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					onChange={handleDriverChange}
					resettable
					hidden={isFieldHidden(`${prefix}_driver`)}
				/>
				<GridInput
					xs={12}
					sm={6}
					md={4}
					lg={2}
					component={PhoneInput}
					source={`${prefix}_driver_phone`}
					disabled={isDisabledForEditing}
					resettable
					hidden={isFieldHidden(`${prefix}_driver_phone`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={4}
					lg={2}
					component={YesNoInput}
					source={`${prefix}_show_doc_for_trans`}
					label={"resources.orders.fields." + prefix + "_show_doc"}
					defaultValue={true}
					disabled={!isDisabledForEditing}
					hidden={isFieldHidden(`${prefix}_show_doc_for_trans`)}
				/>
				{prefix === "etd" && (
					<GridInput
						xs={6}
						sm={6}
						md={4}
						lg={2}
						component={YesNoInput}
						source="is_doc_for_nfsa"
						defaultValue={false}
						disabled={otherFieldsDisabled}
						hidden={isFieldHidden(HideableOrderField.IS_DOC_FOR_NFSA)}
					/>
				)}
			</FormGroupGrid>

			{/* Row 4: Goods & Handling (ETA) or Storage allocation (ETD) */}
			<FormGroupGrid>
				{prefix === "eta" ? (
					<>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							hidden={
								values.service === OrderService.reloadCarCar ||
								isFieldHidden(HideableOrderField.COOLER_TYPE_ID)
							}
							disabled={otherFieldsDisabled}
						>
							<CoolerTypeSelect filter={{ order_service: values.service }} uppercase />
						</GridInput>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="pallets"
							min={0}
							max={100}
							step={10}
							disabled={otherFieldsDisabled || isColdStorageService || isOutgoingPlukk}
							resettable
							hidden={isFieldHidden(HideableOrderField.PALLETS)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="boxes"
							min={0}
							max={100}
							step={5}
							disabled={otherFieldsDisabled || isColdStorageService || isOutgoingPlukk}
							resettable
							hidden={isFieldHidden(HideableOrderField.BOXES)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							type="float"
							digitsCount={2}
							source="kilos"
							min={0}
							max={1000}
							step={100}
							disabled={otherFieldsDisabled || isColdStorageService || isOutgoingPlukk}
							resettable
							hidden={isFieldHidden(HideableOrderField.KILOS)}
						/>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							component={CommodityInput}
							source="commodity_id"
							disabled={otherFieldsDisabled}
							hidden={isFieldHidden(HideableOrderField.COMMODITY_ID)}
						/>
					</>
				) : (
					<>
						{(isIntoPlukkStorage || isCrossDock) && (
							<>
								<GridInput
									xs={usesStorageModule ? 6 : 4}
									sm={usesStorageModule ? 4 : 3}
									md={2}
									lg={2}
									disabled
								>
									<PlaceSelectInput
										source={order_storage_place_field}
										label={`resources.orders.fields.${order_storage_place_field}`}
										terminalId={values["terminal_id"]}
										validate={validateArea}
										resettable
									/>
								</GridInput>
								{!usesStorageModule ? (
									<GridInput
										xs={4}
										sm={3}
										md={2}
										lg={2}
										component={YesNoInput}
										source="is_area_blocked"
										disabled
										hidden={isFieldHidden(HideableOrderField.IS_AREA_BLOCKED)}
									/>
								) : null}
							</>
						)}
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={PrintedDocsInput}
							source="printed_doc"
							defaultValue={false}
							disabled={otherFieldsDisabled}
							hidden={isFieldHidden(HideableOrderField.PRINTED_DOC)}
						/>
						{(isIntoPlukkStorage || isCrossDock) && (
							<>
								<GridInput
									xs={4}
									sm={3}
									md={2}
									lg={2}
									component={SelectEnumInput}
									onChange={handleLabelSourceChange}
									options={{ format: "" }}
									source="label_source"
									key={"label_source"}
									enumName="label_source"
									enumObj={LabelSource}
									hidden={isFieldHidden(HideableOrderField.LABEL_SOURCE)}
								/>
								<GridInput
									xs={4}
									sm={3}
									md={2}
									lg={2}
									component={NumberInputEx}
									options={{ format: "" }}
									source="label"
									disabled={!values["label_source"]}
									resettable
									hidden={isFieldHidden(HideableOrderField.LABEL)}
								/>
							</>
						)}
					</>
				)}
			</FormGroupGrid>
		</ETAETDFormGroup>
	);
};

const getDiffKeys = (obj1, obj2) => {
	const keys = union(Object.keys(obj1), Object.keys(obj2));
	return keys.filter((x) => obj1[x] !== obj2[x]);
};

const fieldRelation = {
	terminal_id: ["port_in", "port_out", "storage_place_id", "area_id"],
	eta_3rd_party: [
		"eta_driver_id",
		"eta_truck_id",
		"eta_trailer_id",
		"eta_driver",
		"eta_truck",
		"eta_trailer",
	],
	etd_3rd_party: [
		"etd_driver_id",
		"etd_truck_id",
		"etd_trailer_id",
		"etd_driver",
		"etd_truck",
		"etd_trailer",
	],
	company_id: ["eta_3rd_party", "etd_3rd_party"],
};
fieldRelation["company_id"] = [
	...fieldRelation["company_id"],
	...fieldRelation["eta_3rd_party"],
	...fieldRelation["etd_3rd_party"],
];

export const FormCustomer = ({ isAddMode, initRecordData, ...props }) => {
	const form = useForm();
	const { permissions } = usePermissions();
	const onFormChange = (formState, a, b) => {
		console.log(formState);
	};
	const { values, initialValues } = useFormState({ onChange: onFormChange });
	const { isFieldHidden } = useTerminalFieldVisibility(values?.terminal_id);
	const { data: terminals } = useTerminalsContext();
	const usesStorageModule = isStorageModuleEnabled(terminals[values["terminal_id"]]);
	const dataProvider = useDataProvider();
	const translate = useTranslate();
	const storageAllocationTitle = translate(
		usesStorageModule
			? "resources.orders.block-label.preparation-allocation"
			: "resources.orders.block-label.storage-allocation"
	);

	React.useEffect(() => {
		if (isAddMode && permissions && permissions.company_id) {
			dataProvider.getOne("customer-companies", { id: permissions.company_id }).then((data) => {
				form.change("commodity_id", data.data.commodity_id);
				form.change("plumb", data.data.plumb);
				form.change("seal", data.data.seal);
				form.change("terminal_id", data.data.terminal_id);
				form.change("is_copy_documents", data.data.copy_docs_on_clone);
			});
		}
	}, [isAddMode, permissions]);

	const [isTerminalDisabled, setIsTerminalDisabled] = React.useState(true);
	const [isStatusDisabled, setIsStatusDisabled] = React.useState(true);
	const [canChangeCustomerAndReference, setCanChangeCustomerAndReference] = React.useState(false);
	const [enabledFields, setEnabledFields] = React.useState();
	const [otherFieldsDisabled, setOtherFieldsDisabled] = React.useState(false);
	const [recordData, setRecordData] = React.useState(initRecordData);

	const isColdStorageService =
		initRecordData?.multiValues["service"]?.length > 0 &&
		initRecordData.multiValues["service"].every((x) => x === OrderService.coldStorage);
	const isIntoPlukkStorage =
		initRecordData?.multiValues["service"]?.length > 0 &&
		initRecordData.multiValues["service"].every((x) => x === OrderService.intoPlukkStorage);
	const isOutgoingPlukk =
		usesStorageModule &&
		initRecordData?.multiValues["service"]?.some(
			(x, i) => x === OrderService.intoPlukkStorage && !!initRecordData.multiValues["etd_date"]?.[i]
		);

	React.useEffect(() => {
		if (initRecordData && initRecordData.multiValues && initRecordData.multiValues["status"]) {
			const _canChangeTerminal = initRecordData.multiValues["status"].every((x) =>
				OrderStatusEditOrderTerminal.includes(x)
			);
			setIsTerminalDisabled(!_canChangeTerminal);

			const _canChangeCustomerAndReference = initRecordData.multiValues["status"].every((x) =>
				OrderStatusEditOrderCustomerAndReference.includes(x)
			);
			setCanChangeCustomerAndReference(_canChangeCustomerAndReference);
			let enabledFields = {};
			if (!isAddMode) {
				if (
					initRecordData.multiValues["status"].every(
						(x) => OrderStatusCustomerEditOrder.indexOf(x) !== -1
					)
				) {
					setIsStatusDisabled(
						initRecordData.multiValues["status"].some(
							(x) => OrderStatusCustomerEdit.indexOf(x) === -1
						)
					);
				} else if (
					initRecordData.multiValues["status"].every(
						(x) => OrderStatusTransporterEditOrder_2.indexOf(x) !== -1
					)
				) {
					enabledFields = {
						notes: true,
						internal_notes: true,
					};
					const eta_3rd_party = initRecordData.multiValues["eta_3rd_party"].every((x) => x);
					if (!eta_3rd_party) {
						for (const key of allowFieldsEtaTrans) {
							enabledFields[key] = true;
						}
					}
					const etd_3rd_party = initRecordData.multiValues["etd_3rd_party"].every((x) => x);
					if (!etd_3rd_party) {
						for (const key of allowFieldsEtdTrans) {
							enabledFields[key] = true;
						}
					}
					if (
						initRecordData.multiValues["status"].every(
							(x) => OrderStatusTransporterEditOrder_1.indexOf(x) !== -1
						)
					) {
						enabledFields["loading_sequence"] = true;
					}
					setEnabledFields(enabledFields);
				} else if (
					initRecordData.multiValues["status"].some(
						(x) => x === OrderStatus.ORDER_COMPLETED || x === OrderStatus.DELETED
					)
				) {
					setEnabledFields({});
				} else {
					setEnabledFields({ notes: true, internal_notes: true });
				}
			}
		}
	}, [initRecordData]);

	React.useEffect(() => {
		setRecordData((prev) => {
			const diffKeys = getDiffKeys(prev.values, values);
			const newState = { ...prev.state };

			for (const key of diffKeys) {
				const isDiff = isMultiDiff(prev.initialValues[key], values[key], prev.state[key]);
				if (isDiff) newState[key] |= MultiChangesStateEnum.IS_DIFF;
				else newState[key] &= ~MultiChangesStateEnum.IS_DIFF;

				if (fieldRelation[key]) {
					fieldRelation[key].forEach((x) => {
						if (isDiff) newState[x] |= MultiChangesStateEnum.NOT_REVERT;
						else newState[x] &= ~MultiChangesStateEnum.NOT_REVERT;
					});
				}
			}

			const newObj = {
				...prev,
				state: newState,
				values,
			};
			return newObj;
		});
	}, [values]);

	React.useEffect(() => {
		setRecordData((prev) => ({
			...prev,
			initialValues,
		}));
	}, [initialValues]);

	if (!permissions) return null;

	const handleLabelSourceChange = (event) => {
		const val = event.target.value;
		if (!val) {
			form.change("label", null);
		} else if (val === LabelSource.pallets) {
			form.change("label", values.pallets);
		} else if (val === LabelSource.boxes) {
			form.change("label", values.boxes);
		}
	};

	const onTerminalChange = (val) => {
		form.change("port_in", null);
		form.change("port_out", null);
		form.change("storage_place_id", null);
		form.change("area_id", null);
		form.change("is_area_blocked", false);
		form.change("is_per", false);
	};

	const handleCbMulti = (type, key) => {
		switch (type) {
			case "revert":
				form.change(key, initialValues[key]);
				switch (key) {
					case "terminal_id":
						handleCbMulti("revert", "storage_place_id");
						handleCbMulti("revert", "area_id");
						handleCbMulti("revert", "port_in");
						handleCbMulti("revert", "port_out");
						break;
					case "storage_place_id":
					case "area_id":
						break;
					default:
						break;
				}
				break;
			case "clear":
				form.change(key, undefined);
				break;
			default:
				break;
		}
	};

	const multiData = { recordData: recordData, cbMulti: handleCbMulti };

	const requiredMulti = (value, values, opt) => {
		if (recordData.values && recordData.initialValues) {
			const key = opt["name"];
			const isDiff = isMultiDiff(recordData.initialValues[key], value, recordData.state[key]);

			if (isDiff) {
				if (isNullUndEmpty(value)) return "Required";
			}
		}
	};

	const requiredValidArr = [requiredMulti];
	const isStatusDraft = values.status === OrderStatus.DRAFT;
	const statusAllowedIds = OrderStatusCustomerEdit.filter((s) => s !== OrderStatus.DRAFT);
	return (
		<GridForm {...props} enabledFields={enabledFields} multiData={multiData}>
			<FormGroupGrid>
				<GridInput
					source="reference"
					sm={3}
					component={canChangeCustomerAndReference ? OrderReferenceInput : ReadOnlyInput}
					disabled={!canChangeCustomerAndReference}
					validate={!isStatusDraft ? requiredValidArr : undefined}
					autoFocus
				/>
				<GridInput
					sm={1}
					source="terminal_id"
					reference="terminal"
					disabled={isTerminalDisabled}
					validate={requiredValidArr}
					onChange={onTerminalChange}
				>
					<ReferenceInput>
						<SelectInput optionText="name" />
					</ReferenceInput>
				</GridInput>
				<GridInput
					source="service"
					sm={3}
					component={ServiceInput}
					validate={!isStatusDraft ? requiredValidArr : undefined}
					allowEmpty={isStatusDraft}
					defaultValue={!isStatusDraft ? OrderService.reloadCarCar : undefined}
					disabled={!isAddMode && isColdStorageService}
					isCouldStorageVisible={isAddMode || isColdStorageService}
				/>
				<GridInput
					sm={1}
					component={YesNoInput}
					source="is_per"
					defaultValue={false}
					hidden={
						isFieldHidden(HideableOrderField.IS_PER) ||
						!perEligibleServices.includes(values.service)
					}
				/>
				<GridInput
					sm={3}
					hidden={isAddMode}
					disabled={isStatusDisabled}
					validate={isStatusDisabled ? undefined : requiredValidArr}
				>
					<ReferenceInput source="status" reference="order-statuses">
						<SelectDisabledInput allowedIds={statusAllowedIds} optionText="name" />
					</ReferenceInput>
				</GridInput>
				<GridInput sm={1} component={YesNoInput} source="priority" defaultValue={false} />
			</FormGroupGrid>

			<ETAETDBlock
				sm={12}
				md={12}
				lg={6}
				isAddMode={isAddMode}
				requiredMulti={requiredMulti}
				prefix="eta"
				title="ETA  —  ARRIVAL"
				permissions={permissions}
				{...props}
				enabledFields={enabledFields}
				multiData={multiData}
				isFieldHidden={isFieldHidden}
				otherFieldsDisabled={otherFieldsDisabled}
				isColdStorageService={isColdStorageService}
				isOutgoingPlukk={isOutgoingPlukk}
				handleLabelSourceChange={handleLabelSourceChange}
			/>
			{!isColdStorageService ? (
				<ETAETDBlock
					sm={12}
					md={12}
					lg={6}
					isAddMode={isAddMode}
					requiredMulti={requiredMulti}
					prefix="etd"
					title="ETD  —  DEPARTURE"
					permissions={permissions}
					{...props}
					enabledFields={enabledFields}
					multiData={multiData}
					isFieldHidden={isFieldHidden}
					otherFieldsDisabled={otherFieldsDisabled}
					isOutgoingPlukk={isOutgoingPlukk}
					handleLabelSourceChange={handleLabelSourceChange}
				/>
			) : (
				<ColdStorageSection
					sm={12}
					md={12}
					lg={6}
					isAddMode={isAddMode}
					isMultiChanges={true}
					{...props}
				/>
			)}

			{!isColdStorageService && (
				<>
					<FormGroupGrid
						xs={12}
						md={6}
						lg={4}
						title={translate("resources.orders.block-label.split-loads")}
						hidden={isFieldHidden(HideableOrderField.SPLIT_LOAD)}
					>
						<GridInput
							xs={4}
							component={YesNoInput}
							source="split_load"
							defaultValue={false}
							hidden={isFieldHidden(HideableOrderField.SPLIT_LOAD)}
						/>
					</FormGroupGrid>
				</>
			)}

			<FormGroupGrid>
				<GridInput
					sm={12}
					md={6}
					component={TextInput}
					source="notes"
					multiline
					rows={6}
					validate={[maxLength(1024)]}
					resettable
				/>
				<GridInput
					sm={12}
					md={6}
					className="red-border"
					component={DangerTextInputForInternalNotes}
					source="internal_notes"
					multiline
					rows={6}
					validate={[maxLength(1000)]}
					disabled
				/>
			</FormGroupGrid>
		</GridForm>
	);
};
